import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { BACKEND_BASE_URL } from '../config/backend';
import { defaultTimeoutFor, shouldRetryOnce, lifecycleConfig, COMPUTE_TIMEOUT_MS, isSafeReadConfig, isKnownOffline, makeOfflineError, isTimeoutError, isOfflineError, isAbortError } from './requestLifecycle';
import { perfLog, perfNow } from './perf';

const API = `${BACKEND_BASE_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// =========================================================================
// Client-side date validation
// =========================================================================
// Fast-fail guard for any API helper that takes a YYYY-MM-DD string (or a
// list of them). Returns a REJECTED PROMISE so the existing try/catch (or
// .catch) on every caller handles it normally — this MUST NOT throw
// synchronously, because a synchronous throw inside a React event handler
// from a non-async caller using .then/.catch would bypass the .catch and
// bubble to <Sentry.ErrorBoundary>, which would show the full-screen
// "Something went wrong" fallback.
//
// The rejected Error carries `.caller`, `.invalidValue`, `.stack` so the
// source of the bad input is traceable in Sentry breadcrumbs / console.
// -------------------------------------------------------------------------
const _DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function _makeBadDateError(caller, invalidValue) {
  const err = new Error(
    `[${caller}] invalid date ${JSON.stringify(invalidValue)} — expected YYYY-MM-DD`
  );
  err.name = 'InvalidDateError';
  err.caller = caller;
  err.invalidValue = invalidValue;
  // Log loudly so the caller stack is visible in device console / Sentry
  // breadcrumbs without tripping the React ErrorBoundary.
  try {
    console.error('[api.js] Bad date rejected client-side:', err.message, '\n', err.stack);
  } catch (_) { /* ignore */ }
  return err;
}

function _isValidDateStr(s) {
  return typeof s === 'string' && _DATE_RE.test(s);
}

// Returns null if OK, or a rejected Promise if invalid.
function _checkDate(value, caller) {
  if (!_isValidDateStr(value)) {
    return Promise.reject(_makeBadDateError(caller, value));
  }
  return null;
}

function _checkDateList(values, caller) {
  if (!Array.isArray(values) || values.length === 0) {
    return Promise.reject(_makeBadDateError(caller, values));
  }
  const bad = values.find(v => !_isValidDateStr(v));
  if (bad !== undefined) {
    return Promise.reject(_makeBadDateError(caller, bad));
  }
  return null;
}

// Session token storage key
const SESSION_TOKEN_KEY = 'fitxess_session_token';

// Store session token
export function setSessionToken(token) {
  if (token) {
    localStorage.setItem(SESSION_TOKEN_KEY, token);
    console.log('[Auth] Session token stored');
  } else {
    localStorage.removeItem(SESSION_TOKEN_KEY);
    console.log('[Auth] Session token cleared');
  }
}

// Get stored session token
export function getSessionToken() {
  return localStorage.getItem(SESSION_TOKEN_KEY);
}

// Check if token exists
export function hasSessionToken() {
  return !!localStorage.getItem(SESSION_TOKEN_KEY);
}

// [PERF] temporary per-request timing. Path only (no query string, no
// headers, no payloads) — never logs tokens or user data.
function perfPath(config) {
  return String((config && config.url) || '').split('?')[0];
}
function perfMethod(config) {
  return String((config && config.method) || 'get').toUpperCase();
}
function perfEnd(config, fields) {
  try {
    const start = config && config.__fxPerfStart;
    perfLog('[API][END] ' + perfMethod(config) + ' ' + perfPath(config), Object.assign({
      duration_ms: typeof start === 'number' ? perfNow() - start : undefined,
      retried: config && config.__fxRetried ? 1 : 0,
    }, fields));
  } catch (_e) { /* never fail a request because of a log */ }
}

// Add Authorization header to all requests
api.interceptors.request.use((config) => {
  config.__fxPerfStart = perfNow();
  perfLog('[API][START] ' + perfMethod(config) + ' ' + perfPath(config), {
    t: config.__fxPerfStart,
    retry: config.__fxRetried ? 1 : 0,
  });
  // Known offline: fail a safe read NOW (no 15s wait, no auto-retry — see
  // isRetryableReadError). Writes / AI / compute keep their normal lifecycle.
  if (isSafeReadConfig(config) && isKnownOffline()) {
    throw makeOfflineError(config);
  }
  // Shared request lifecycle: every request has a FINITE timeout (safe reads
  // and writes get different defaults; an explicit per-call timeout wins), so
  // no network-backed spinner can wait forever. See utils/requestLifecycle.js.
  if (!config.timeout) config.timeout = defaultTimeoutFor(config);
  const token = getSessionToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // TestFlight diagnostics: log every API call so Xcode device console
  // reveals whether the auth header is being attached and which URL the
  // native app is actually hitting. Strip token value but log presence
  // and length so we can tell stale vs missing vs present.
  try {
    const url = (config.baseURL || '') + (config.url || '');
    const platform = Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web';
    console.log('[api][req]', config.method?.toUpperCase(), url, {
      hasToken: !!token,
      tokenLen: token ? token.length : 0,
      platform,
    });
  } catch (_e) { /* never fail a request because of a log */ }
  return config;
});

// =============== 402 SUBSCRIPTION INTERCEPTOR ===============
// When any API call returns HTTP 402 with a structured error code
// ("subscription_required" or "daily_limit_reached"), we fire a global
// handler that opens the paywall. The SubscriptionProvider registers the
// handler via setSubscriptionRequiredHandler() at mount time; this keeps
// api.js decoupled from React.
let _subscriptionRequiredHandler = null;

export function setSubscriptionRequiredHandler(handler) {
  _subscriptionRequiredHandler = handler;
}

api.interceptors.response.use(
  (response) => {
    perfEnd(response.config, { status: response.status });
    // TestFlight diagnostics: log the status of every API response.
    try {
      const url = (response.config?.baseURL || '') + (response.config?.url || '');
      console.log('[api][res]', response.status, response.config?.method?.toUpperCase(), url);
    } catch (_e) { /* swallow */ }
    return response;
  },
  (error) => {
    const status = error?.response?.status;
    perfEnd(error && error.config, {
      status: status || undefined,
      error: isOfflineError(error) ? 'offline'
        : isAbortError(error) ? 'aborted'
          : isTimeoutError(error) ? 'timeout'
            : status ? undefined : (error?.code || 'network'),
    });
    // TestFlight diagnostics: log failures with enough detail to root-cause
    // 401 vs CORS vs network errors from the iOS device console.
    try {
      const url = (error.config?.baseURL || '') + (error.config?.url || '');
      console.error('[api][err]', status || 'NETWORK', error.config?.method?.toUpperCase(), url, {
        message: error.message,
        code: error.code,
        detail: error.response?.data?.detail,
      });
    } catch (_e) { /* swallow */ }

    // Shared lifecycle: ONE automatic re-send for a safe/idempotent read that
    // failed in transport (timeout / network, no response). Never 4xx/5xx,
    // never a cancelled request, never a write or AI generation.
    if (shouldRetryOnce(error && error.config, error)) {
      error.config.__fxRetried = true;
      return api.request(error.config);
    }

    // Auto-recover from 401 (stale / invalid / cross-env token).
    //
    // The earlier implementation used `window.location.replace('/login…')`
    // which, on iOS WKWebView (Capacitor), caused multiple concurrent 401s
    // to issue overlapping hard-navigations that interrupted React's
    // render cycle — manifesting as white-screen on Plan / Progress /
    // Settings / Grocery tabs after the bottom nav rendered.
    //
    // New approach: clear storage + clear in-memory zustand user state +
    // raise a one-shot sessionStorage flag. ProtectedRoute already listens
    // to the zustand `user` value and navigates to /login via React Router
    // when it goes null — that's the correct, WebView-safe redirect path.
    // Login.jsx reads the sessionStorage flag and surfaces the banner.
    if (status === 401) {
      const reqUrl = error.config?.url || '';
      const isAuthEndpoint =
        reqUrl.startsWith('/auth/validate-session') ||
        reqUrl.startsWith('/auth/logout') ||
        reqUrl.startsWith('/auth/magic-link') ||
        reqUrl.startsWith('/auth/apple') ||
        reqUrl.startsWith('/auth/session');
      const hadToken = !!localStorage.getItem(SESSION_TOKEN_KEY);
      // Idempotent guard: with several APIs in flight on app open, every
      // concurrent 401 would otherwise trigger the clear path. The first
      // 401 wins; the rest just reject.
      if (!isAuthEndpoint && hadToken && !window.__fitxessSessionCleared) {
        window.__fitxessSessionCleared = true;
        console.warn('[api] 401 on', reqUrl, '— clearing stale session, ProtectedRoute will redirect');
        try {
          localStorage.removeItem(SESSION_TOKEN_KEY);
          localStorage.removeItem('fitxess-user-storage');
          localStorage.removeItem('fitxess_user');
          // One-shot flag read by Login.jsx to show the amber banner.
          // Cleared by Login.jsx on mount.
          sessionStorage.setItem('fitxess_session_expired', '1');
        } catch (_e) { /* swallow */ }
        // Dynamically clear in-memory zustand. Avoids the circular import
        // (`userStore` already imports `clearAuth` from this module) and
        // forces ProtectedRoute / Login.restore to see `user = null` on
        // the very next render. The store module is cached by ESM after
        // the first import, so subsequent triggers are effectively free.
        import('../store/userStore')
          .then((mod) => {
            try {
              const store = mod.default;
              store.setState({ user: null, profile: null });
            } catch (_e) { /* swallow */ }
          })
          .catch(() => { /* swallow — clearAuth already happened */ });
      }
    }

    if (status === 402) {
      const detail = error.response?.data?.detail || error.response?.data || {};
      const code = detail?.error;
      if (
        code === 'subscription_required' ||
        code === 'daily_limit_reached' ||
        code === 'planning_around_logged_meals_requires_pro' ||
        code === 'future_date_requires_pro'
      ) {
        try {
          if (typeof _subscriptionRequiredHandler === 'function') {
            _subscriptionRequiredHandler({ code, detail });
          } else {
            console.warn('[api] 402 received but no subscription handler registered:', code);
          }
        } catch (e) {
          console.warn('[api] subscription handler threw:', e);
        }
      }
    }
    return Promise.reject(error);
  }
);

// Helper to get current platform for API calls
function getCurrentPlatform() {
  if (Capacitor.isNativePlatform()) {
    return Capacitor.getPlatform();
  }
  return 'web';
}

/**
 * Refresh auth state by calling /api/auth/me
 * Single source of truth for fetching current user
 * Returns: { user, profile } or throws error
 */
export async function refreshAuth() {
  const token = getSessionToken();
  if (!token) {
    console.log('[Auth] refreshAuth: No token present');
    throw new Error('No session token');
  }
  
  console.log('[Auth] refreshAuth: Fetching user from /api/auth/me');
  const response = await api.get('/auth/me');
  const user = response.data;
  
  console.log('[Auth] refreshAuth: Success', {
    userId: user.user_id,
    onboardingComplete: user.onboarding_complete
  });
  
  // Also fetch profile if user exists
  let profile = null;
  try {
    const profileResponse = await api.get('/profile');
    profile = profileResponse.data;
  } catch (e) {
    console.log('[Auth] refreshAuth: Profile fetch failed (non-critical)');
  }
  
  return { user, profile };
}

/**
 * Clear auth state completely
 */
export function clearAuth() {
  setSessionToken(null);
}

export const authAPI = {
  createSession: (sessionId) => api.post('/auth/session', null, { params: { session_id: sessionId } }),
  validateSession: (sessionToken) => api.post('/auth/validate-session', null, { params: { session_token: sessionToken } }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  deleteAccount: () => api.delete('/auth/delete-account'),
  sendMagicLink: (email, redirectUrl) => api.post('/auth/magic-link', { 
    email, 
    redirect_url: redirectUrl,
    platform: getCurrentPlatform()
  }),
  verifyMagicLink: (token) => api.post('/auth/verify-magic-link', { token }),
  googleMobileSignIn: (idToken) => api.post('/auth/google/mobile', { id_token: idToken }),
};

export const profileAPI = {
  getProfile: (opts = null) => api.get('/profile', requestLifecycle(opts)),
  // Consolidated onboarding endpoint - saves all data in one transaction
  completeOnboarding: (data) => api.put('/onboarding', data),
  // New unified profile endpoints
  saveProfile: (profile) => api.post('/profile', profile),  // For onboarding
  updateProfile: (profile) => api.patch('/profile', profile),  // For settings
  regenerateTargets: () => api.post('/profile/regenerate-targets'),  // Recalculate macros
  // Why nutrition is / isn't ready: { targets_ready, next_step, missing_profile_fields, can_regenerate, ... }
  getNutritionReadiness: () => api.get('/profile/nutrition-readiness'),
  // Legacy endpoints (still work, use new calculation)
  updateMacros: (macros) => api.put('/profile/macros', macros),
  setGoal: (goal) => api.post('/profile/goal', { goal }),
  generateMacros: (quiz) => api.post('/profile/generate-macros', quiz),
  updatePreferences: (prefs) => api.put('/profile/preferences', prefs),
  updatePlanning: (planning) => api.put('/profile/planning', planning),
  updateActivity: (activity) => api.put('/profile/activity', activity),
  getCookingPreferences: () => api.get('/profile/cooking-preferences'),
  updateCookingPreferences: (prefs) => api.put('/profile/cooking-preferences', prefs),
};

// Canonical food preferences (broad groups + exact-food concepts). The backend
// is the only authority for which groups/concepts exist and are selectable.
export const foodPreferencesAPI = {
  getOptions: () => api.get('/food-preferences/options'),
  search: (q, purpose, limit = 8) => api.get('/food-preferences/search', { params: { q, purpose, limit } }),
  get: () => api.get('/profile/food-preferences'),
  // body: { expected_revision, allergens: [{kind,id}], dislikes: [{kind,id}], keep_unresolved }
  save: (body) => api.put('/profile/food-preferences', body),
};

export const mealAPI = {
  getMeals: (category) => api.get('/meals', { params: { category } }),
  getMeal: (mealId, opts = null) => api.get(`/meals/${mealId}`, requestLifecycle(opts)),
  createCustomMeal: (meal) => api.post('/meals/custom', meal),
  
  // Custom meal management (for manually created/logged meals)
  getCustomMeal: (mealId, planId = null) => 
    api.get(`/meals/custom/${mealId}`, { params: { plan_id: planId || undefined } }),
  updateCustomMeal: (mealId, updates) => 
    api.put(`/meals/custom/${mealId}`, updates),
  // ITEM-19: server-owned rebuild-aware "Log Again" — clones authoritative stored snapshots into a new
  // rebuild-backed logged meal (no recompute, no legacy math). Idempotent per (user, operation_id).
  rebuildLogAgain: (payload) => api.post('/ai-log/draft/rebuild-v2/log-again', payload),
  uploadMealImage: async (mealId, file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post(`/meals/custom/${mealId}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // User's saved custom meals
  getMyMeals: () => api.get('/meals/my-meals'),
  deleteMyMeal: (mealId) => api.delete(`/meals/my-meals/${mealId}`),
  getFavorites: () => api.get('/meals/favorites'),
  addFavorite: (mealId) => api.post('/meals/favorites', { meal_id: mealId }),
  removeFavorite: (mealId) => api.delete(`/meals/favorites/${mealId}`),
  getExclusions: () => api.get('/meals/exclusions'),
  addExclusion: (mealId) => api.post('/meals/exclusions', { meal_id: mealId }),
  recordTasteSignal: (mealId, sessionId, action) =>
    api.post('/meals/taste-signal', { meal_id: mealId, session_id: sessionId, action }),
  getTasteSetupSample: () => api.get('/meals/taste-setup/sample'),
  getTasteSetupDemoSample: () => api.get('/meals/taste-setup/demo-sample'),
  completeTasteSetup: () => api.post('/meals/taste-setup/complete'),
  removeExclusion: (mealId) => api.delete(`/meals/exclusions/${mealId}`),
  importRecipe: (recipe) => api.post('/meals/import-recipe', recipe),
  getSmartFill: (date, mealType) => api.post('/meals/smart-fill', { date, meal_type: mealType }, readOnlyPost()),
  // Search core foods for Edit Meal (uses same methodology as admin)
  searchCoreFoods: (query, limit = 20) => api.get('/foods/core-search', { params: { q: query, limit } }),
  // Calculate macros for edited ingredients
  calculateEditedMacros: (mealId, ingredientAdjustments, portionMultiplier = 1.0) => 
    api.post('/meals/calculate-edited', {
      meal_id: mealId,
      ingredient_adjustments: ingredientAdjustments,
      portion_multiplier: portionMultiplier
    }, readOnlyPost()),
};

export const mealPlanAPI = {
  getDailyPlan: (date, opts = null) => api.get('/meal-plans/daily', Object.assign({ params: { date } }, requestLifecycle(opts))),
  getDailySummary: (date, opts = null) => api.get('/meal-plans/daily-summary', Object.assign({ params: { date } }, requestLifecycle(opts))),
  getWeeklyPlan: (startDate) => api.get('/meal-plans/weekly', { params: { start_date: startDate } }),
  createMealPlan: (plan) => api.post('/meal-plans', plan),
  addMealPlan: (plan) => api.post('/meal-plans', plan),
  updateMealPlan: (planId, status) => api.patch(`/meal-plans/${planId}`, { status }),
  deleteMealPlan: (planId, clientToday = null) =>
    api.delete(`/meal-plans/${planId}`, clientToday ? { params: { client_today: clientToday } } : undefined),
  // Group-aware, atomic, idempotent bulk clear of PENDING meals for dates.
  bulkClear: (dates, operationId = null) =>
    api.post('/meal-plans/bulk-clear', { dates, operation_id: operationId }),
  // Canonical leftover/scaling context for a plan row (backend-derived; robust
  // to null/legacy prep_scale_factor). MealView uses this on refresh/deep link.
  getMealPlanContext: (planId, clientToday) => api.get(`/meal-plans/${planId}/context`, {
    params: clientToday ? { client_today: clientToday } : undefined,
  }),
  // Read-only ranked replacement recipes for a linked pair (regeneration in
  // suggestion mode). Backend resolves the pair + both-day context and ranks
  // via the existing planner scorer. Does NOT write.
  pairAlternatives: (planId, count = 6) =>
    api.post(`/meal-plans/${planId}/pair-alternatives`, null, readOnlyPost({ params: { count } })),
  // Pair-safe replacement: swaps the recipe on BOTH linked rows atomically.
  swapPair: (planId, newMealId, opts = {}) =>
    api.post('/meal-plans/swap-pair', {
      plan_id: planId, new_meal_id: newMealId,
      expected_meal_id: opts.expectedMealId || null,
      operation_id: opts.operationId || null,
      client_today: opts.clientToday || null,
    }),
  // Atomic CHILD-ONLY leftover replacement (parent cook untouched). Used when a
  // leftover's parent cook is finalized/past — the leftover becomes terminal
  // "replaced" and a fresh standalone Lunch (source "swap") is created.
  replaceLeftoverChild: (childPlanId, newMealId, opts = {}) =>
    api.post('/meal-plans/replace-leftover-child', {
      child_plan_id: childPlanId, new_meal_id: newMealId,
      client_today: opts.clientToday,
      expected_meal_id: opts.expectedMealId || null,
      operation_id: opts.operationId || null,
    }),
  // Update meal plan with edited ingredients and macros
  updateMealPlanEdits: (planId, edits) => api.patch(`/meal-plans/${planId}/edits`, edits),
  getMealPlanEdits: (planId) => api.get(`/meal-plans/${planId}/edits`),
  // Aliases for consistency across the app
  updateMealPlanStatus: (planId, status) => api.patch(`/meal-plans/${planId}`, { status }),
  removeMealPlan: (planId) => api.delete(`/meal-plans/${planId}`),
  // Browse meals with macros for weekly planning
  browseMealsWithMacros: (params) => api.post('/browse-meals/macro-aware', params, readOnlyPost()),
};

export const nutritionAPI = {
  getDailySummary: (date, opts = null) => api.get('/nutrition/daily-summary', Object.assign({ params: { date } }, requestLifecycle(opts))),
  getWeeklySummary: () => api.get('/nutrition/weekly'),
  getWeeklyHistory: (weeks = 4) => api.get('/nutrition/weekly-history', { params: { weeks } }),
};

export const groceryAPI = {
  getGroceryList: (weekStart) => api.get('/grocery-list', { params: { week_start: weekStart } }),
};

export const pantryAPI = {
  getCommonItems: () => api.get('/pantry/common'),
  getIngredients: () => api.get('/pantry/ingredients'),
  getPantry: (opts = null) => api.get('/pantry', requestLifecycle(opts)),
  updatePantry: (pantryData) => api.put('/pantry', pantryData),
  // Add NEW items: {items: [{name, food_id?, category?}], origin}. The server
  // decides canonical vs custom and returns {results, pantry}.
  addItems: (items, origin = 'pantry') => api.post('/pantry/items', { items, origin }),
  // My Pantry global search: recipe-used Core Foods first, then classified
  // Core Foods; never USDA. Each result carries its Pantry section.
  search: (q, limit = 25) => api.get('/pantry/search', { params: { q, limit } }),
};

export const planningAPI = {
  getSummary: (dates, opts = null) => api.post('/planning/summary', { dates }, requestLifecycle(Object.assign({ idempotent: true }, opts))),
  // POST-shaped READS (no writes): idempotent -> shared timeout + one transport retry.
  getGrocery: (dates, opts = null) => api.post('/planning/grocery', { dates }, requestLifecycle(Object.assign({ idempotent: true }, opts))),
  autoFix: (date) => api.post('/planning/auto-fix', { date }),
  suggestSwaps: (date, planIds = null) => api.post('/planning/suggest-swaps', { date, plan_ids: planIds }, computePost()),
  applySuggestion: (date, suggestionType, mealType, mealId, mealName, planId = null, operationId = null, expectedMealId = null) =>
    api.post('/planning/apply-suggestion', null, {
      params: Object.assign({ date, suggestion_type: suggestionType, meal_type: mealType, meal_id: mealId, meal_name: mealName, plan_id: planId, operation_id: operationId },
        expectedMealId ? { expected_meal_id: expectedMealId } : {})
    }),
};

// Optional per-request lifecycle for screens that own their loads (Step-by-
// Step): an AbortController `signal` to cancel a retired request and a
// `timeout` (ms) so a stalled request ends in a recoverable error. Only these
// two keys are passed through; callers that omit `opts` keep today's behavior.
function requestLifecycle(opts) {
  return lifecycleConfig(opts);
}

// POST-shaped requests that do NOT write (audited against the backend handlers).
// Everything else sent as POST keeps the write lifecycle: never auto-retried.
//   readOnlyPost: ordinary query -> read timeout + ONE transport-failure retry.
//   computePost:  read-only but expensive (candidate search / optimizer /
//                 suggestions) -> finite COMPUTE_TIMEOUT_MS, retry:false.
function readOnlyPost(extra, opts) {
  return Object.assign({}, extra || {}, requestLifecycle(Object.assign({ idempotent: true }, opts)));
}
function computePost(extra, opts) {
  return Object.assign({}, extra || {}, requestLifecycle(Object.assign({ timeout: COMPUTE_TIMEOUT_MS }, opts, { retry: false })));
}

export const guidedPlanningAPI = {
  getMeals: (date, mealType, maxResults = 12, cookware = null, maxCookTime = null, query = null, opts = null) => api.post('/guided-planning/meals', Object.assign({
    date, 
    meal_type: mealType, 
    max_results: maxResults,
    cookware: cookware,
    max_cook_time: maxCookTime
  }, query ? { query: query } : {}), requestLifecycle(opts)),
  getStatus: (date, opts = null) => api.get(`/guided-planning/status/${date}`, requestLifecycle(opts)),
  // Same authoritative status, read only to reconcile another date's weekly
  // completed/check state (kept separate from the canonical day load).
  getCompletionStatus: (date) => api.get(`/guided-planning/status/${date}`),
};

export const browseMealsAPI = {
  // `draft` (Meal Review Swap only): the unsaved Review day the Swap is judged against.
  getMacroAware: (date, category = null, filters = {}, replacePlanId = null, targetMealType = null, draft = null) => api.post('/browse-meals/macro-aware', Object.assign({
    date,
    category,
    replace_plan_id: replacePlanId || null,
    ...(targetMealType ? { target_meal_type: targetMealType } : {}),
    ...(draft ? { draft } : {}),
  }, filters || {}), readOnlyPost()),
  getFilterOptions: () => api.get('/browse-meals/filter-options'),
};

export const smartFillAPI = {
  getSuggestions: (date, mealType) => {
    const bad = _checkDate(date, 'smartFillAPI.getSuggestions(date)');
    if (bad) return bad;
    return api.post('/meals/smart-fill', { date, meal_type: mealType }, readOnlyPost());
  },
  auto: (dates, preferences, pantryItems = [], cookingPreferences = null, filters = null, mealsToKeep = null, excludeMealIds = null, draft = false, includeLeftovers = false) => {
    const bad = _checkDateList(dates, 'smartFillAPI.auto(dates)');
    if (bad) return bad;
    // Send the device's LOCAL date so the backend future-date gate can keep
    // the user's current day Free while charging Pro for planning ahead.
    const _now = new Date();
    const client_today = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_now.getDate()).padStart(2, '0')}`;
    return api.post('/smart-fill/auto', {
      dates,
      preferences,
      pantry_items: pantryItems,
      cooking_preferences: cookingPreferences,
      filters: filters,
      meals_to_keep: mealsToKeep,
      exclude_meal_ids: excludeMealIds,
      draft,
      include_leftovers: includeLeftovers,
      client_today
    });
  },
  getFeatureFlags: () => api.get('/config/features'),
  confirm: (plans, dates, replaceExisting = true, operationId = null, replacePlanIds = null) => {
    const bad = _checkDateList(dates, 'smartFillAPI.confirm(dates)');
    if (bad) return bad;
    return api.post('/smart-fill/confirm', {
      plans, dates, replace_existing: replaceExisting, operation_id: operationId,
      replace_plan_ids: replacePlanIds
    });
  },
  alternatives: (date, mealType, dayMeals, weekMeals = null, excludeMealIds = null, count = 6, pairContext = null) => {
    const bad = _checkDate(date, 'smartFillAPI.alternatives(date)');
    if (bad) return bad;
    return api.post('/smart-fill/alternatives', {
      date, meal_type: mealType, day_meals: dayMeals,
      week_meals: weekMeals, exclude_meal_ids: excludeMealIds, count, pair_context: pairContext
    }, computePost());
  },
  regenerateDay: (date, lockedMeals = null, weekMeals = null, excludeMealIds = null) => {
    const bad = _checkDate(date, 'smartFillAPI.regenerateDay(date)');
    if (bad) return bad;
    return api.post('/smart-fill/draft/regenerate-day', {
      date, locked_meals: lockedMeals, week_meals: weekMeals, exclude_meal_ids: excludeMealIds
    });
  },
  evaluateDraft: (plans, dates, replacePlanIds = []) => api.post('/smart-fill/draft/evaluate', {
    plans, dates, replace_plan_ids: replacePlanIds
  }, readOnlyPost()),
  getGapSuggestions: (dates, gaps) => {
    const bad = _checkDateList(dates, 'smartFillAPI.getGapSuggestions(dates)');
    if (bad) return bad;
    return api.post('/fill-gaps/suggestions', { dates, gaps }, computePost());
  },
  optimize: (dates, gaps) => {
    const bad = _checkDateList(dates, 'smartFillAPI.optimize(dates)');
    if (bad) return bad;
    return api.post('/smart-fill/optimize', { dates, gaps }, computePost());
  },
};

export const foodsAPI = {
  // Legacy endpoint (backward compatible)
  getFoods: (category, search) => api.get('/foods', { params: { category, search } }),
  getFood: (foodId) => api.get(`/foods/${foodId}`),
  calculateMacros: (foodId, quantity, unit) => api.post('/foods/calculate-macros', null, readOnlyPost({ params: { food_id: foodId, quantity, unit } })),
  
  // New USDA-integrated endpoints
  searchFoods: (query, limit = 25) => api.get('/foods/search', { params: { q: query, limit } }),
  getRecentFoods: (limit = 10) => api.get('/foods/recent', { params: { limit } }),
  cacheFromUSDA: (fdcId) => api.post('/foods/cache-from-usda', null, { params: { fdc_id: fdcId } }),
  calculateMacrosNew: (foodId, quantity, unit) => api.post('/foods/calculate-macros', null, readOnlyPost({ params: { food_id: foodId, quantity, unit } })),
  
  // Search history
  recordSearch: (query, foodId, foodName) => api.post('/foods/search-history', null, { params: { query, food_id: foodId, food_name: foodName } }),
  getSearchHistory: (limit = 10) => api.get('/foods/search-history', { params: { limit } }),
  clearSearchHistory: () => api.delete('/foods/search-history'),
};

// =============== ADMIN API ===============

export const adminAPI = {
  // Auth check
  checkAdmin: () => api.get('/auth/admin-check'),
  
  // Meal CRUD
  listMeals: (search, status, source, groupId, uniquenessGroup) => api.get('/admin/meals', { 
    params: { search, status, source, group_id: groupId, uniqueness_group: uniquenessGroup } 
  }),
  getMeal: (mealId) => api.get(`/admin/meals/${mealId}`),
  createMeal: (data) => api.post('/admin/meals', data),
  updateMeal: (mealId, data) => api.put(`/admin/meals/${mealId}`, data),
  deleteMeal: (mealId) => api.delete(`/admin/meals/${mealId}`),
  duplicateMeal: (mealId) => api.post(`/admin/meals/${mealId}/duplicate`),
  validateMeal: (data) => api.post('/admin/meals/validate', data),
  bulkDeleteMeals: (mealIds) => api.post('/admin/meals/bulk-delete', { meal_ids: mealIds }),
  
  // Meal Groups
  listMealGroups: () => api.get('/admin/meal-groups'),
  createMealGroup: (data) => api.post('/admin/meal-groups', data),
  updateMealGroup: (groupId, data) => api.put(`/admin/meal-groups/${groupId}`, data),
  deleteMealGroup: (groupId) => api.delete(`/admin/meal-groups/${groupId}`),
  addMealsToGroup: (mealIds, groupId) => api.post('/admin/meals/add-to-group', { meal_ids: mealIds, group_id: groupId }),
  removeMealsFromGroup: (mealIds) => api.post('/admin/meals/remove-from-group', { meal_ids: mealIds }),
  
  // Tags
  listTags: () => api.get('/admin/tags'),
  listTagsDetailed: () => api.get('/admin/tags/detailed'),
  renameTag: (oldName, newName) => api.post('/admin/tags/rename', { old_name: oldName, new_name: newName }),
  deleteTag: (tagName) => api.post('/admin/tags/delete', { tag_name: tagName }),
  bulkApplyTags: (mealIds, tagsToAdd, tagsToRemove) => api.post('/admin/tags/bulk-apply', { 
    meal_ids: mealIds, 
    tags_to_add: tagsToAdd, 
    tags_to_remove: tagsToRemove 
  }),
  
  // Uniqueness Groups (Repeat Rules)
  listUniquenessGroups: () => api.get('/admin/uniqueness-groups'),
  getUniquenessGroupsOverview: () => api.get('/admin/uniqueness-groups/overview'),
  createUniquenessGroup: (name) => api.post('/admin/uniqueness-groups', { name }),
  updateUniquenessGroup: (groupName, noRepeatSameDay, cooldownDays) => 
    api.put(`/admin/uniqueness-groups/${encodeURIComponent(groupName)}`, { 
      no_repeat_same_day: noRepeatSameDay,
      cooldown_days: cooldownDays
    }),
  deleteUniquenessGroup: (groupName) => api.delete(`/admin/uniqueness-groups/${encodeURIComponent(groupName)}`),
  bulkAssignUniquenessGroup: (mealIds, uniquenessGroup, noRepeatSameDay, cooldownDays) => 
    api.post('/admin/uniqueness-groups/bulk-assign', { 
      meal_ids: mealIds, 
      uniqueness_group: uniquenessGroup,
      no_repeat_same_day: noRepeatSameDay,
      cooldown_days: cooldownDays
    }),
  
  // Core foods search (includes portion_options)
  searchCoreFoods: (query, limit = 20) => api.get('/admin/core-foods/search', { params: { q: query, limit } }),

  // Core Foods management surface (list/search + curate metadata)
  listCoreFoods: (params = {}) => api.get('/admin/core-foods', { params }),
  getCoreFood: (foodId) => api.get(`/admin/core-foods/${foodId}`),
  createCoreFood: (data) => api.post('/admin/core-foods', data),
  updateCoreFood: (foodId, data) => api.put(`/admin/core-foods/${foodId}`, data),
  getPreferenceTaxonomy: () => api.get('/admin/preference-taxonomy'),

  // Users & Access (complimentary Free Pro grants)
  searchUsers: (q) => api.get('/admin/users/search', { params: { q } }),
  listGrants: (status) => api.get('/admin/complimentary-grants', { params: { status } }),
  createGrants: (emails, reason, expiresAt) => api.post('/admin/complimentary-grants', { emails, reason, expires_at: expiresAt || null }),
  uploadGrantsCsv: (formData) => api.post('/admin/complimentary-grants/csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  revokeGrants: (grantIds, emails) => api.post('/admin/complimentary-grants/revoke', { grant_ids: grantIds || null, emails: emails || null }),

  
  // Seeding
  seedPortionOptions: () => api.post('/admin/seed-portion-options'),
};

// =============== SUBSCRIPTION API ===============

export const subscriptionAPI = {
  // Get current subscription status
  getStatus: () => api.get('/subscription/status'),
  
  // Check if user can access a specific feature
  checkFeature: (feature) => api.post('/subscription/check-feature', null, readOnlyPost({ params: { feature } })),
  
  // Restore purchases (sync with backend after RevenueCat restore)
  // forceRefresh: explicit purchase / restore / Sync / 402 recovery -> the
  // backend skips its 60s reconcile cache and reads RevenueCat fresh. The
  // passive launch sync omits it.
  restore: (appUserId, opts) => api.post('/subscription/restore', {
    app_user_id: appUserId,
    force_refresh: !!(opts && opts.forceRefresh),
  }),
};

// =============== LOG FOOD API ===============
//
// Phase 0 (Feb 2026) removed Spoonacular and MLKit barcode scanning. The
// brand search, generic / branded search, barcode lookup, food-nutrition,
// and nearby-restaurant methods that lived here are gone. Phase 1 will
// reintroduce search / branded / barcode / restaurant flows backed by
// FatSecret with a native AVFoundation scanner.
//
// Only the two endpoints required for manual / custom / my-meals logging
// remain.

export const logFoodAPI = {
  // Log a food item to meal plan. Sends the device's LOCAL date (client_today)
  // so the backend classifies completed vs planned on the user's local-day
  // boundary (never UTC midnight).
  logFood: (food, date, mealType, quantity = 1) => {
    const _n = new Date();
    const client_today = `${_n.getFullYear()}-${String(_n.getMonth() + 1).padStart(2, '0')}-${String(_n.getDate()).padStart(2, '0')}`;
    return api.post('/log-food/log', food, {
      params: { date, meal_type: mealType, quantity, client_today },
    });
  },

  // Get user's saved meals for logging
  getMyMeals: () => api.get('/log-food/my-meals'),

  // --- Phase 1 search endpoints (PHASE_1_SEARCH_CONTRACT.md) ---

  // Recently logged foods, optionally scoped to a tab and/or filtered by q.
  // scope: 'foods' | 'restaurants' | 'meals' | 'barcode' | 'manual' | undefined
  getRecentFoods: (params = {}) => api.get('/log-food/recent-foods', { params }),

  // Foods tab — FatSecret. Returns 503 until ENABLE_FATSECRET=true.
  searchFoods: (params = {}) => api.get('/log-food/search/foods', { params }),

  // Restaurants tab — FatSecret. Returns chain list.
  searchRestaurants: (params = {}) => api.get('/log-food/search/restaurants', { params }),

  // Restaurant chain menu drilldown.
  getRestaurantMenu: (chainId, params = {}) =>
    api.get('/log-food/restaurants/' + encodeURIComponent(chainId) + '/menu', { params }),

  // Meals tab — Mongo, live.
  searchMeals: (params = {}) => api.get('/log-food/search/meals', { params }),

  // Manual tab "saved foods" search — Mongo, live.
  searchMyFoods: (params = {}) => api.get('/log-food/search/my-foods', { params }),

  // Barcode UPC lookup — FatSecret. Returns 503 until ENABLE_FATSECRET=true.
  lookupBarcode: (upc) => api.post('/log-food/barcode/lookup', { upc }, readOnlyPost()),

  // Full FatSecret food details (all serving_options) for a selected search
  // result. Basic-tier search omits servings, so we enrich on selection.
  getFoodDetails: (externalId) => api.get('/log-food/food/' + encodeURIComponent(externalId)),
};

export default api;
